import { z } from 'zod';

import { NO_POS } from '../ast.ts';
import { TypeMismatchError } from '../errors.ts';
import { opMeta } from '../op-meta.ts';
import { dateAwareEquals, toComparableNumber } from '../ops/transform/date-utils.ts';
import { arrayContainsMember } from '../ops/transform/string-funcs.ts';
import { booleanCol } from '../schema/types.ts';
import { ColumnRef } from '../value.ts';
import { defineBinary, defineChain, numChainParams } from './helpers.ts';

// Comparison. `<`, `<=`, `>`, `>=`, `=` are chains; `!=` is strict pairwise.

export const lessThan = z
  .object({ name: z.literal('<'), params: numChainParams })
  .describe('Strictly-less-than chain. (< a b c) is true iff a < b < c.')
  .register(opMeta, {
    terseGroup: 'compare',
    example: { expr: '(< 1 2 3)', result: 'true' },
    ...defineChain<number>((a, b) => a < b, toComparableNumber),
  });

export const lessEq = z
  .object({ name: z.literal('<='), params: numChainParams })
  .describe('Less-than-or-equal chain. (<= a b c) is true iff a <= b <= c.')
  .register(opMeta, {
    terseGroup: 'compare',
    example: { expr: '(<= 1 1 2)', result: 'true' },
    ...defineChain<number>((a, b) => a <= b, toComparableNumber),
  });

export const greater = z
  .object({ name: z.literal('>'), params: numChainParams })
  .describe('Strictly-greater-than chain. (> a b c) is true iff a > b > c.')
  .register(opMeta, {
    terseGroup: 'compare',
    example: { expr: '(> 3 2 1)', result: 'true' },
    ...defineChain<number>((a, b) => a > b, toComparableNumber),
  });

export const greaterEq = z
  .object({ name: z.literal('>='), params: numChainParams })
  .describe('Greater-than-or-equal chain. (>= a b c) is true iff a >= b >= c.')
  .register(opMeta, {
    terseGroup: 'compare',
    example: { expr: '(>= 3 3 2)', result: 'true' },
    ...defineChain<number>((a, b) => a >= b, toComparableNumber),
  });

// Equality is JS-strict (`===`) for number/boolean — `(= 1 "1")`,
// `(= isActive "true")` are false. Strings compare case-INsensitively
// (LLM-facing tool — casing must be forgiving). Dates: if either side
// is genuinely date-shaped (a `Date` cell or an ISO `YYYY-MM-DD…`
// string) the two are compared as instants, so `(= day "2025-10-01")`
// against a `timestamp` column matches instead of silently never
// matching. See `dateAwareEquals` (the SSOT, incl. push-down caveat).
export const equals = z
  .object({
    name: z.literal('='),
    params: z.array(z.unknown()).min(2),
  })
  .describe(
    'Equality chain. (= a b c) is true iff a = b = c — strict for number/boolean, case-insensitive for strings; date-aware when either side is a date/timestamp.',
  )
  .register(opMeta, {
    terseGroup: 'compare',
    example: { expr: '(= 1 1 1)', result: 'true' },
    ...defineChain((a, b) => dateAwareEquals(a, b)),
  });

export const notEquals = z
  .object({
    name: z.literal('!='),
    params: z.tuple([z.unknown(), z.unknown()]),
  })
  .describe(
    'Inequality. (!= a b) is true iff a ≠ b — the negation of `=` (strict for number/boolean, case-insensitive for strings, date-aware for dates/timestamps).',
  )
  .register(opMeta, {
    terseGroup: 'compare',
    example: { expr: '(!= 1 2)', result: 'true' },
    ...defineBinary((a, b) => !dateAwareEquals(a, b), undefined, booleanCol),
  });

const describeNonArrayMembers = (members: unknown): string => {
  if (members instanceof ColumnRef) {
    return 'an unresolved (col …) reference';
  }
  if (members === null) {
    return 'null';
  }
  return typeof members;
};

// Set membership. The second arg is a vector AST; eager evaluation
// resolves it to a JS array before reaching apply, and `compileExpr`'s
// vector branch produces the per-row array. CDF push-down maps
// `in` to its `in` filter leaf — see `expr/ops/knowledge-graph/pushdown.ts`.
export const inOp = z
  .object({
    name: z.literal('in'),
    params: z.tuple([z.unknown(), z.array(z.unknown())]),
  })
  .describe(
    'Set membership. (in v [a b c]) is true iff v equals some element — same equality as `=` (strict for number/boolean, case-insensitive for strings, date-aware for dates/timestamps).',
  )
  .register(opMeta, {
    terseGroup: 'compare',
    example: { expr: '(in "a" ["a" "b" "c"])', result: 'true' },
    ...defineBinary<unknown>(
      (value, members) => {
        // The schema types the 2nd operand as a vector, but the per-row
        // `compileExpr` path is not re-validated against it: an
        // unresolved `(col other-ds col)` set-reference (or a per-row
        // struct cell mistakenly used as a set) reaches here as a
        // non-array. Fail with a typed, actionable error instead of the
        // opaque `.some is not a function`.
        if (!Array.isArray(members)) {
          throw new TypeMismatchError(
            describeNonArrayMembers(members),
            'a literal vector — e.g. a resolved `(col other-ds col)` set-reference',
            '(in v <members>)',
            NO_POS,
          );
        }
        // Type-directed on the SUBJECT (1st arg), mirroring `contains`: an
        // ARRAY / relation-list container — e.g. the normalized misuse
        // `(in (col % rel externalId) <set>)` — tests element OVERLAP against
        // the set, agreeing with the pushed `containsAny`. Both funnel through
        // the SINGLE membership helper (`arrayContainsMember`) per the D2 SSOT
        // invariant. A scalar subject keeps the unchanged `value ∈ members`.
        if (Array.isArray(value)) {
          return members.some((m) => arrayContainsMember(value, m));
        }
        return members.some((m) => dateAwareEquals(m, value));
      },
      undefined,
      booleanCol,
    ),
  });
